import type { ReactNode } from 'react';
import { ErrorMessage, Hint, RequiredMarker, cx, describedBy } from '../../components/idsk';
import { fallsBackToSlovak, text, type FieldDef, type FieldError, type JsonValue, type LocalizedText } from '../types';
import { cellId } from './messages';

type Cells = Record<string, JsonValue>;

function isObject(v: JsonValue | undefined): v is Cells {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** The text of a cell of a TABLE value ('' when it is not filled). */
export function cellText(value: JsonValue | undefined, row: string, column: string): string {
  const cells = isObject(value) ? value[row] : undefined;
  const v = isObject(cells) ? cells[column] : undefined;
  return typeof v === 'string' ? v : '';
}

/** The value with the text of one cell changed; an emptied cell (and row) is left out, no cell at all: undefined. */
export function withCell(value: JsonValue | undefined, row: string, column: string, cell: string): JsonValue | undefined {
  const next: Cells = isObject(value) ? { ...value } : {};
  const cells: Cells = isObject(next[row]) ? { ...(next[row] as Cells) } : {};
  if (cell === '') delete cells[column];
  else cells[column] = cell;
  if (Object.keys(cells).length) next[row] = cells;
  else delete next[row];
  return Object.keys(next).length ? next : undefined;
}

export interface TableInputProps {
  id: string;
  field: FieldDef;
  lang: 'sk' | 'en';
  error?: FieldError;
  errorText?: string;
  required: boolean;
  disabled: boolean;
  disabledHint: ReactNode;
  value: JsonValue | undefined;
  formGroupClassName?: string;
  extraDescribedBy?: string;
  onDraft: (value: JsonValue | undefined) => void;
  onCommit: () => void;
}

/**
 * A TABLE field (D24): a fieldset whose legend names the table; the first row holds the column headers, the first
 * column the row headers, every other cell is a text input named by its row and column header (aria-labelledby).
 * The cells at fault are marked like the parts of a date input; a wide table scrolls inside its container.
 */
export function TableInput({ id, field, lang, error, errorText, required, disabled, disabledHint, value, formGroupClassName, extraDescribedBy, onDraft, onCommit }: TableInputProps) {
  const table = field.table;
  if (!table) return null;
  const hint = field.hint ? text(field.hint, lang) : '';
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = errorText ? `${id}-error` : undefined;
  const legendId = `${id}-legend`;
  // headers carry "--" so their ids never equal a cell's {id}-{row}-{column}
  const rowHeaderId = (key: string) => `${id}--row-${key}`;
  const columnHeaderId = (key: string) => `${id}--column-${key}`;
  const langOf = (label: LocalizedText) => (fallsBackToSlovak(label, lang) ? 'sk' : undefined);
  const max = field.constraints?.maxLength ?? 255;
  // the cells the error is about: the empty ones, the too long ones, else all of them
  const atFault = (row: string, column: string) => {
    if (!error) return false;
    const cell = cellText(value, row, column);
    if (error.code === 'table.incomplete') return cell.trim() === '';
    if (error.code === 'maxLength') return cell.length > max;
    return true;
  };
  return (
    <div className={cx('govuk-form-group', errorText && 'govuk-form-group--error', formGroupClassName)}>
      <fieldset className="govuk-fieldset" aria-describedby={describedBy(extraDescribedBy, hintId, errorId)}>
        <legend className="govuk-fieldset__legend govuk-fieldset__legend--s" lang={langOf(field.label)}>
          <span id={legendId}>{text(field.label, lang)}</span>
          <RequiredMarker required={required} />
        </legend>
        {hint && (
          <Hint id={hintId} lang={field.hint ? langOf(field.hint) : undefined}>
            {hint}
          </Hint>
        )}
        {errorText && <ErrorMessage id={errorId}>{errorText}</ErrorMessage>}
        {disabledHint}
        <div className="app-table-container">
          <table className="govuk-table app-input-table" id={id} aria-labelledby={legendId}>
            <thead className="govuk-table__head">
              <tr className="govuk-table__row">
                <th scope="col" className="govuk-table__header" lang={langOf(table.corner)}>
                  {text(table.corner, lang)}
                </th>
                {table.columns.map((c) => (
                  <th scope="col" className="govuk-table__header" id={columnHeaderId(c.key)} key={c.key} lang={langOf(c.label)}>
                    {text(c.label, lang)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="govuk-table__body">
              {table.rows.map((r) => (
                <tr className="govuk-table__row" key={r.key}>
                  <th scope="row" className="govuk-table__header" id={rowHeaderId(r.key)} lang={langOf(r.label)}>
                    {text(r.label, lang)}
                  </th>
                  {table.columns.map((c) => {
                    const invalid = atFault(r.key, c.key);
                    return (
                      <td className="govuk-table__cell" key={c.key}>
                        <input
                          className={cx('govuk-input app-input-table__input', invalid && 'govuk-input--error')}
                          id={cellId(id, r.key, c.key)}
                          type="text"
                          autoComplete="off"
                          aria-labelledby={`${rowHeaderId(r.key)} ${columnHeaderId(c.key)}`}
                          aria-invalid={invalid ? true : undefined}
                          aria-disabled={disabled ? true : undefined}
                          readOnly={disabled}
                          value={cellText(value, r.key, c.key)}
                          onChange={(e) => onDraft(withCell(value, r.key, c.key, e.target.value))}
                          onBlur={onCommit}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </fieldset>
    </div>
  );
}

/** A filled TABLE read-only (summaries): the same headers and the texts of the cells; the label is the caption. */
export function TableView({ field, value, lang }: { field: FieldDef; value: JsonValue | undefined; lang: 'sk' | 'en' }) {
  const table = field.table;
  if (!table) return null;
  const langOf = (label: LocalizedText) => (fallsBackToSlovak(label, lang) ? 'sk' : undefined);
  return (
    <div className="app-table-container">
      <table className="govuk-table">
        <caption className="govuk-table__caption govuk-table__caption--s" lang={langOf(field.label)}>
          {text(field.label, lang)}
        </caption>
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header" lang={langOf(table.corner)}>
              {text(table.corner, lang)}
            </th>
            {table.columns.map((c) => (
              <th scope="col" className="govuk-table__header" key={c.key} lang={langOf(c.label)}>
                {text(c.label, lang)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {table.rows.map((r) => (
            <tr className="govuk-table__row" key={r.key}>
              <th scope="row" className="govuk-table__header" lang={langOf(r.label)}>
                {text(r.label, lang)}
              </th>
              {table.columns.map((c) => (
                <td className="govuk-table__cell" key={c.key}>
                  {cellText(value, r.key, c.key)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
